import { API_KEY, BASE_URL, TRAKT_API_KEY, TRAKT_BASE_URL } from './apiConfig';

const CONSTANTS = {
  // API & Country Codes
  TMDB_KOREAN_COUNTRY_CODE: 'KR',
  TVMAZE_KOREAN_COUNTRY_CODE: 'KR',
  TRAKT_KOREAN_COUNTRY_CODE: 'kr',

  // Filtering Rules
  MAX_EPISODES_TOTAL_SHOW: 100,
  MAX_EPISODES_IN_SEASON: 40,
  MAX_EPISODES_TVMAZE: 50,
  MAX_SEASONS_TVMAZE: 5,
  
  // Caching Time-To-Live (TTL) in Minutes
  CACHE_TTL_TODAY_MINUTES: 60,
  CACHE_TTL_PAST_DAYS_MINUTES: 7 * 24 * 60,
  CACHE_TTL_SUCCESS_TMDB_MINUTES: 7 * 24 * 60,
  CACHE_TTL_FAILURE_TMDB_MINUTES: 24 * 60,

  // Other
  SCHEDULE_FETCH_DAYS: 30,
  PHILIPPINES_TIMEZONE: 'Asia/Manila',
  GENRES_TO_EXCLUDE: ['Variety', 'Reality', 'Talk Show', 'Game Show'],
  AIRTIME_DELAY_MINUTES: 60,
};

const cache = {
  get: (key) => {
    const item = localStorage.getItem(key);
    if (!item) return null;
    const { value, expiry } = JSON.parse(item);
    if (Date.now() > expiry) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  },
  set: (key, value, ttlInMinutes) => {
    const expiry = Date.now() + ttlInMinutes * 60 * 1000;
    localStorage.setItem(key, JSON.stringify({ value, expiry }));
  },
};

export const getPhilippinesDate = (daysOffset = 0) => {
  const now = new Date();
  const phtDate = new Date(now.toLocaleString('en-US', { timeZone: CONSTANTS.PHILIPPINES_TIMEZONE }));
  phtDate.setDate(phtDate.getDate() + daysOffset);
  const year = phtDate.getFullYear();
  const month = String(phtDate.getMonth() + 1).padStart(2, '0');
  const day = String(phtDate.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getPhilippinesTime = () => {
  const now = new Date();
  const phtDate = new Date(now.toLocaleString('en-US', { timeZone: CONSTANTS.PHILIPPINES_TIMEZONE }));
  const hours = String(phtDate.getHours()).padStart(2, '0');
  const minutes = String(phtDate.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const isCurrentTimeAfterAirtime = (currentTime, airtime) => {
  if (!airtime) return true;
  const [currentHour, currentMinute] = currentTime.split(':').map(Number);
  const [airHour, airMinute] = airtime.split(':').map(Number);
  const currentTimeInMinutes = currentHour * 60 + currentMinute;
  const airtimeInMinutes = airHour * 60 + airMinute;
  const delayedAirtimeInMinutes = airtimeInMinutes + CONSTANTS.AIRTIME_DELAY_MINUTES;
  return currentTimeInMinutes >= delayedAirtimeInMinutes;
};

const normalizeTitle = (str) => str.toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();

const _fetchFromImdbId = async (imdbId, season) => {
  if (!imdbId) return null;
  try {
    const findRes = await fetch(`${BASE_URL}/find/${imdbId}?api_key=${API_KEY}&external_source=imdb_id`);
    if (!findRes.ok) return null;

    const findData = await findRes.json();
    if (findData.tv_results?.length > 0) {
      const tmdbId = findData.tv_results[0].id;
      const detailRes = await fetch(`${BASE_URL}/tv/${tmdbId}?api_key=${API_KEY}&append_to_response=season/${season}`);
      return detailRes.ok ? await detailRes.json() : null;
    }
    return null;
  } catch {
    return null;
  }
};

const _fetchFromTitleSearch = async (showName, season) => {
  try {
    const searchRes = await fetch(`${BASE_URL}/search/tv?api_key=${API_KEY}&query=${encodeURIComponent(showName)}`);
    if (!searchRes.ok) return null;

    const searchData = await searchRes.json();
    const normalizedSearchTitle = normalizeTitle(showName);

    for (const result of searchData.results) {
      if (!result.origin_country?.includes(CONSTANTS.TMDB_KOREAN_COUNTRY_CODE)) continue;

      const normalizedResultTitle = normalizeTitle(result.name || '');
      const normalizedOriginalTitle = normalizeTitle(result.original_name || '');
      
      const isMatch = (normalizedResultTitle.includes(normalizedSearchTitle) || normalizedSearchTitle.includes(normalizedResultTitle)) ||
                      (normalizedOriginalTitle.includes(normalizedSearchTitle) || normalizedSearchTitle.includes(normalizedOriginalTitle));

      if (isMatch) {
        const detailRes = await fetch(`${BASE_URL}/tv/${result.id}?api_key=${API_KEY}&append_to_response=season/${season}`);
        if (detailRes.ok) {
          const detailData = await detailRes.json();
          if (detailData.origin_country?.includes(CONSTANTS.TMDB_KOREAN_COUNTRY_CODE)) {
            return detailData;
          }
        }
      }
    }
    return null;
  } catch {
    return null;
  }
};

const _findTmdbIdViaTrakt = async (showName) => {
    try {
        const headers = {
            'Content-Type': 'application/json',
            'trakt-api-version': '2',
            'trakt-api-key': TRAKT_API_KEY,
        };
        const res = await fetch(`${TRAKT_BASE_URL}/search/show?query=${encodeURIComponent(showName)}&extended=full`, { headers });
        if (!res.ok) return null;

        const searchData = await res.json();
        if (!searchData || searchData.length === 0) return null;

        const koreanResult = searchData.find(item =>
            item.show?.country === CONSTANTS.TRAKT_KOREAN_COUNTRY_CODE && item.show?.ids?.tmdb
        );
        if (koreanResult) {
            return koreanResult.show.ids.tmdb;
        }

        const firstResultWithId = searchData.find(item => item.show?.ids?.tmdb);
        return firstResultWithId ? firstResultWithId.show.ids.tmdb : null;
    } catch {
        return null;
    }
};

const _fetchFromTmdbId = async (tmdbId, season) => {
    if (!tmdbId) return null;
    try {
        const detailRes = await fetch(`${BASE_URL}/tv/${tmdbId}?api_key=${API_KEY}&append_to_response=season/${season}`);
        return detailRes.ok ? await detailRes.json() : null;
    } catch {
        return null;
    }
};

export const fetchTMDBDataForShow = async (tvmazeShow, episodeInfo = {}) => {
  try {
    const tvmazeId = tvmazeShow.id;
    const cacheKey = `tmdb-data-${tvmazeId}`;
    const cachedData = cache.get(cacheKey);
    if (cachedData) return cachedData;

    const seasonToCheck = episodeInfo.season || 1;
    let foundTmdbData = null;

    foundTmdbData = await _fetchFromImdbId(tvmazeShow.externals?.imdb, seasonToCheck);

    if (!foundTmdbData) {
      foundTmdbData = await _fetchFromTitleSearch(tvmazeShow.name, seasonToCheck);
    }

    if (!foundTmdbData) {
      const tmdbIdFromTrakt = await _findTmdbIdViaTrakt(tvmazeShow.name);
      if (tmdbIdFromTrakt) {
        foundTmdbData = await _fetchFromTmdbId(tmdbIdFromTrakt, seasonToCheck);
      }
    }

    if (!foundTmdbData || !foundTmdbData.id) {
      cache.set(cacheKey, null, CONSTANTS.CACHE_TTL_FAILURE_TMDB_MINUTES);
      return null;
    }
    
    if (foundTmdbData.vote_average === 0) {
        return null;
    }
    
    if (foundTmdbData.genres) {
        const tmdbGenres = foundTmdbData.genres.map(g => g.name);
        const hasExcludedGenre = CONSTANTS.GENRES_TO_EXCLUDE.some(excludedGenre => tmdbGenres.includes(excludedGenre));
        if (hasExcludedGenre) {
            return null;
        }
    }

    if (foundTmdbData.number_of_episodes > CONSTANTS.MAX_EPISODES_TOTAL_SHOW) return null;
    const seasonData = foundTmdbData[`season/${seasonToCheck}`];
    const episodeCount = seasonData?.episodes?.length || 0;
    if (episodeCount >= CONSTANTS.MAX_EPISODES_IN_SEASON) return null;

    const finalData = {
      ...foundTmdbData,
      tmdb_id: foundTmdbData.id,
      media_type: 'tv',
      seasonEpisodeData: {
        episodeCount,
        hasMany: episodeCount >= CONSTANTS.MAX_EPISODES_IN_SEASON,
        seasonData,
      },
    };
    
    cache.set(cacheKey, finalData, CONSTANTS.CACHE_TTL_SUCCESS_TMDB_MINUTES);
    return finalData;

  } catch (error) {
    console.error(`Error fetching TMDB data for show ${tvmazeShow.name}:`, error);
    return null;
  }
};

export const fetchKoreanDramas = async () => {
  try {
    const currentPhilippinesTime = getPhilippinesTime();
    const todayDate = getPhilippinesDate(0);
    const dates = Array.from({ length: CONSTANTS.SCHEDULE_FETCH_DAYS }, (_, i) => getPhilippinesDate(-i));

    const schedulePromises = dates.map(async (date) => {
        const cacheKey = `tvmaze-schedule-${date}`;
        const isToday = date === todayDate;
        const cachedSchedule = cache.get(cacheKey);
        if (cachedSchedule) return cachedSchedule;

        try {
            const res = await fetch(`https://api.tvmaze.com/schedule?country=${CONSTANTS.TVMAZE_KOREAN_COUNTRY_CODE}&date=${date}`);
            if (!res.ok) return [];
            const data = await res.json();
            const mappedData = data.map(item => ({ ...item, fetchDate: date }));
            
            const ttl = isToday ? CONSTANTS.CACHE_TTL_TODAY_MINUTES : CONSTANTS.CACHE_TTL_PAST_DAYS_MINUTES;
            cache.set(cacheKey, mappedData, ttl);
            return mappedData;
        } catch {
            return [];
        }
    });

    const allShows = (await Promise.all(schedulePromises)).flat();

    const filteredShows = allShows.filter(item => {
      const isDrama = !CONSTANTS.GENRES_TO_EXCLUDE.some(genre => item.show?.genres?.includes(genre));
      const isNotLongRunning = item.number <= CONSTANTS.MAX_EPISODES_TVMAZE && item.season <= CONSTANTS.MAX_SEASONS_TVMAZE;
      const hasAired = item.fetchDate !== todayDate || isCurrentTimeAfterAirtime(currentPhilippinesTime, item.airtime);
      return item.show?.name && isDrama && isNotLongRunning && hasAired;
    });

    const showGroups = new Map();
    filteredShows.forEach(item => {
      const showName = item.show.name;
      if (!showGroups.has(showName)) {
        showGroups.set(showName, []);
      }
      showGroups.get(showName).push(item);
    });

    const uniqueShows = [];
    showGroups.forEach((episodes) => {
      episodes.sort((a, b) => {
        const dateComparison = new Date(b.fetchDate) - new Date(a.fetchDate);
        if (dateComparison !== 0) return dateComparison;
        return (b.number || 0) - (a.number || 0);
      });
      uniqueShows.push(episodes[0]);
    });

    const dramaPromises = uniqueShows.map(async (showItem) => {
      const tmdbData = await fetchTMDBDataForShow(showItem.show, {
        season: showItem.season,
        number: showItem.number
      });

      if (tmdbData) {
        const episodeCount = tmdbData.seasonEpisodeData.episodeCount;
        const isSeasonFinale = episodeCount > 0 && showItem.number === episodeCount;
        return {
          ...tmdbData,
          fetchDate: showItem.fetchDate,
          airtime: showItem.airtime,
          episodeInfo: {
            number: showItem.number,
            season: showItem.season,
            name: showItem.name,
            isSeasonFinale: isSeasonFinale
          },
          daysFromToday: Math.floor(Math.abs(new Date(todayDate) - new Date(showItem.fetchDate)) / (1000 * 60 * 60 * 24))
        };
      }
      return null;
    });

    const fetchedDramas = (await Promise.all(dramaPromises)).filter(Boolean);
    
    const dramaMap = new Map();
    fetchedDramas.forEach(drama => {
      const existing = dramaMap.get(drama.tmdb_id);
      if (!existing || new Date(drama.fetchDate) > new Date(existing.fetchDate)) {
        dramaMap.set(drama.tmdb_id, drama);
      }
    });
    
    const koreanDramas = Array.from(dramaMap.values());
    koreanDramas.sort((a, b) => {
      const aIsVeryRecent = a.daysFromToday <= 3;
      const bIsVeryRecent = b.daysFromToday <= 3;
      if (aIsVeryRecent && !bIsVeryRecent) return -1;
      if (!aIsVeryRecent && bIsVeryRecent) return 1;
      
      const dateComparison = new Date(b.fetchDate) - new Date(a.fetchDate);
      if (dateComparison !== 0) return dateComparison;
      
      return (b.episodeInfo?.number || 0) - (a.episodeInfo?.number || 0);
    });

    return koreanDramas;

  } catch (error) {
    console.error('An error occurred in fetchKoreanDramas:', error);
    return [];
  }
};